import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon } from "@radix-ui/react-icons";
import { Button, Callout, Card, Flex, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import * as yup from 'yup';
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { useGetCurrentAcademicYearQuery } from "../../hooks/useGetCurrentAcademicYearQuery";
import { useGetTeacheIdByUserId } from "../../hooks/useGetTeacheIdByUserId";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { getAssessments } from "../../services/school/assessment/getAssessments";
import { createStudentSubjectGrade } from "../../services/school/student/createStudent";
import { getStudentsAssessments } from "../../services/school/student/getStudentsAssessments";
import { getStudentsFromClass } from "../../services/school/student/getStudentsFromClass";
import { createStudentAssessment } from "../../services/school/teacher/createStudentAssessment";
import { getTeacherClasses, getTeacherSubjects } from "../../services/school/teacher/getTeachers";
import { updateStudentAssessment } from "../../services/school/teacher/updateStudentAssessment";
import { StudentSubjectGradeType } from "../../types/StudentSubjectGrade";
import { APPROVED, MINIMUM_GRADE, REPROVED } from "../../utils/constants";
import { StudentGradesForm } from "./StudentGradesForm";
import { calculateFinalGradeV2 } from "./calculateFinalGradeV2";

export const createStudentAssessmentSchema = yup.object().shape({
  idSubject: yup.string().required('Disciplina é obrigatória'),
  idClass: yup.string().required('Turma é obrigatória'),
  studentAssessments: yup.array().of(
    yup.object().shape({
      idStudent: yup.string().required('ID do estudante é obrigatório'),
      assessments: yup.array().of(
        yup.object().shape({
          idAssessment: yup.string().required('ID da avaliação é obrigatório'),
          grade: yup
            .number()
            .typeError('A nota deve ser um número válido')
            .min(0, 'A nota deve ser no mínimo 0')
            .max(20, 'A nota deve ser no máximo 20')
            .required('Nota é obrigatória')
          ,
          status: yup.boolean().required('Status é obrigatório'),
          reason: yup.string(),
          isPublished: yup.boolean().required('Status é obrigatório'),
        })
      )
    })
  )
});
export const AddStudentGrades = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { control, handleSubmit, setValue, getValues, formState: { errors } } = useForm({
    resolver: yupResolver(createStudentAssessmentSchema),
    defaultValues: { idClass: "", idSubject: "", studentAssessments: [] },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });
  const mutationCreateStudentSubjectGrade = useMutation({ mutationFn: createStudentSubjectGrade, mutationKey: "mutationCreateStudentSubjectGrade" })
  const mutationCreateStudentAssessment = useMutation({ mutationFn: createStudentAssessment, mutationKey: "createStudentAssessment" })
  const mutationUpdateStudentAssessment = useMutation({ mutationFn: updateStudentAssessment, mutationKey: "updateStudentAssessment" })
  const loadingState = mutationCreateStudentSubjectGrade.isLoading
    || mutationCreateStudentAssessment.isLoading
    || mutationUpdateStudentAssessment.isLoading;

  const { data: currentAcademicYear, isLoading: isLoadingCurrentAcademicYear, isError: errorCurrentAcademicYear } = useGetCurrentAcademicYearQuery();
  const { UserId } = useSelector((state: RootState) => state.login.userData)
  const { data: teacherData, isLoading: isLoadingTeacherData } = useGetTeacheIdByUserId(UserId);
  const { isLoading: isLoadingClasses, refetch: fetchTeacherClasses, data: classes, isError: isErrorTeacherClasses } = useQuery(
    ['fetchTeacherClasses'],
    async () => {
      try {
        const classes = await getTeacherClasses({ PageNumber: 1, PageSize: 1000, IdTeacher: teacherData?.idTeacher });
        return classes;
      } catch (error) {
      }
    },
    {
      enabled: !!teacherData?.idTeacher,
    }
  );


  const { isLoading: isLoadingSubjects, refetch: fetchTeacherSubjects, data: subjects, isError: isErrorTeacherSubjects } = useQuery(
    ['fetchTeacherSubjects'],
    async () => {
      try {
        const subjects = await getTeacherSubjects({ PageNumber: 1, PageSize: 1000, IdTeacher: teacherData?.idTeacher, IdClass: getValues('idClass') });
        return subjects;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const { isLoading: isLoadingAssessments, refetch: fetchTeacherAssessments, data: assessments, isError: isErrorTeacherAssessments } = useQuery(
    ['fetchTeacherAssessments'],
    async () => {
      try {
        const assessments = await getAssessments({ PageNumber: 1, PageSize: 1000, IdTeacher: teacherData?.idTeacher, IdClass: getValues('idClass'), IdSubject: getValues('idSubject') });
        return assessments;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const { isLoading: isLoadingStudentsFromClass, refetch: fetchStudentsFromClass, data: students, isError: isErrorStudentsFromClass } = useQuery(
    ['fetchStudentsFromClass'],
    async () => {
      try {
        const students = await getStudentsFromClass({ PageNumber: 1, PageSize: 1000, IdClass: getValues('idClass'), IdSubject: getValues('idSubject') });
        return students;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const { isLoading: isLoadingStudentAssessments, refetch: fetchStudentAssessments, data: studentAssessments, isError: isErrorStudentAssessments } = useQuery(
    ['fetchStudentAssessments'],
    async () => {
      try {
        const studentsAssessments = await getStudentsAssessments({ PageNumber: 1, PageSize: 1000, IdClass: getValues('idClass'), IdSubject: getValues('idSubject'), IdTeacher: teacherData?.idTeacher });
        return studentsAssessments;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const mapStudentsAssessments = (idStudent?: string) => {
    const existingStudentAssessments = studentAssessments?.items.filter(sa => sa.idStudent === idStudent);
    return assessments?.items.map(assessment => {
      const existingAssessment = existingStudentAssessments?.find(sa => sa.idAssessment === assessment.id);
      const _assessment =
        existingAssessment ? {
          ...existingAssessment,
        } : {
          idAssessment: assessment?.id!,
          grade: 0,
          status: true,
          reason: '',
          isPublished: true
        };
      return _assessment;
    }) || [];
  };




  useEffect(() => {
    if (students?.items) {
      setValue('studentAssessments', [...students.items].sort((a, b) =>
        (a.student || "").localeCompare(b.student || "")
      ).map((student) => ({
        idStudent: student.idStudent,
        assessments: mapStudentsAssessments(student?.idStudent),
      })));
    }
  }, [students?.items, assessments?.items, studentAssessments?.items, setValue]);

  if (!teacherData || !teacherData.idTeacher) {
    return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Text>
            Entre como docente &nbsp;
            <Link to={routes.dashboard} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
          </Text>
        </Callout.Root>

      </Flex>
    );
  }

  const handleChangeClass = (idClass: string) => {
    setValue('idClass', idClass);
    fetchTeacherSubjects();
  };

  const handleChangeSubject = (idSubject: string) => {
    setValue('idSubject', idSubject);
    fetchTeacherAssessments();
    fetchStudentsFromClass();
    fetchStudentAssessments();
  };

  const onSubmit = handleSubmit(async (data) => {
    const mappedDataAssessments = mapStudentAssessments(data as any)


    try {
      const assessmentsUpdate = mappedDataAssessments.filter(mda => mda?.id);
      const assessmentsSave = mappedDataAssessments.filter(mda => !mda?.id);
      await Promise.all(
        assessmentsSave.map(assessment =>
          mutationCreateStudentAssessment.mutateAsync(assessment)
        )
      );
      if (assessmentsUpdate.length > 0) {
        await Promise.all(
          assessmentsUpdate.map(assessment =>
            mutationUpdateStudentAssessment.mutateAsync(assessment)
          )
        );
      }

      try {
        const grades = mapStudentGrades(data as any) as StudentSubjectGradeType[]
        await Promise.all(grades.map(grade => mutationCreateStudentSubjectGrade.mutateAsync(grade)))
      } catch (error) {
        console.error({ error })
      }
      toast.success("Notas lançadas");
      queryClient.invalidateQueries('fetchStudentsFromClass');
      queryClient.invalidateQueries('fetchTeacherAssessments');
      queryClient.invalidateQueries('fetchStudentAssessments');
      await fetchTeacherAssessments();
      await fetchStudentsFromClass();
      await fetchStudentAssessments();


    } catch (err: any) {
      toast.error(`${err?.message}`);
    }
  });

  const mapStudentAssessments = (data: { studentAssessments: any[]; }) => {
    const mappedData: { idStudent: any; idAssessment: any; grade: any; reason: any; status: any, id?: any, isPublished: any }[] = [];

    data.studentAssessments.forEach((student: { assessments: any[]; idStudent: any; }) => {
      student.assessments.forEach((assessment: { idAssessment: any; grade: any; reason: any; status: any, id?: any, isPublished: any }) => {
        mappedData.push({
          idStudent: student.idStudent,
          idAssessment: assessment.idAssessment,
          grade: assessment.grade,
          reason: assessment.reason,
          status: assessment.status,
          id: assessment?.id,
          isPublished: assessment.isPublished
        });
      });
    });
    return mappedData;
  };

  const mapStudentGrades = (data: { studentAssessments: any[]; }) => {
    const mappedData = data.studentAssessments.map((student: { assessments: any[]; idStudent: any; }) => {
      const grade = calculateFinalGradeV2(student.assessments.filter(a => a.idStudent === student.idStudent), assessments?.items!);
      const _class = classes?.items.find(i => i.id === getValues('idClass'));
      return {
        idStudent: student.idStudent,
        idSubject: getValues('idSubject'),
        idAcademicYear: currentAcademicYear?.id,
        grade,
        result: grade >= MINIMUM_GRADE ? APPROVED : REPROVED,
        level: _class?.level,
        semester: _class?.semester,
        idCourse: _class?.idCourse

      }
    });
    return mappedData;
  };
  const handleBack = () => {
    navigate(routes.dashboard)
  }
  console.log({ t: classes?.items.find(c => c.id === getValues('idClass')) })

  return (
    <Card>

      <Flex direction='column' gap='4'>
        <form onSubmit={onSubmit}>
          <Flex gap='3' direction='column'>

            <Card size='1'>
              <Flex direction='column' gap="2">
                <Flex gap='3'>
                  <SelectComponent name="idClass" label="Turma" control={control} errors={errors} style={{ width: '100%' }}
                    options={classes?.items.map(_class => {
                      return {
                        label: _class.description,
                        value: _class.id ?? ""
                      }
                    })}
                    onSelect={handleChangeClass}
                    itemsLoading={isLoadingClasses}
                    itemsError={isErrorTeacherClasses}
                  />
                  <SelectComponent name="idSubject" label="Disciplina" control={control} errors={errors} style={{ width: '100%' }}
                    options={subjects?.items.map(subject => {
                      return {
                        label: subject.description,
                        value: subject.id ?? ""
                      }
                    })}
                    itemsLoading={isLoadingSubjects}
                    itemsError={isErrorTeacherSubjects}
                    onSelect={handleChangeSubject}
                  />
                </Flex>
              </Flex>
            </Card>

            {getValues('idSubject') && (
              <StudentGradesForm
                students={[...students?.items || []].sort((a, b) =>
                  (a.student || "").localeCompare(b.student || "")
                )}
                assessments={assessments?.items || []}
                control={control}
                errors={errors}
                classe={classes?.items.find(c => c.id === getValues('idClass'))}
                subject={subjects?.items.find(s => s.id === getValues('idSubject'))}
                //course={courses?.items.find(s => s.id === getValues('idSubject'))}
                currentAcademicYear={currentAcademicYear}
                canPublishResults={true}
              />
            )}
            <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
              <Button color='red'
                disabled={loadingState}
                onClick={() => {
                  handleBack();
                }}
              >
                <ChevronLeftIcon /> Voltar
              </Button>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </Card>
  )
}